import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LT_FETCH } from 'bl/loggers.bl';
import FLAG from 'config/flag.singleton';
import STORE from 'config/store.singleton';
import MS from 'constants/time/ms.const';
import { AUTH_API } from 'middleware/api';
import beVersion4 from 'middleware/beVersion4';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import delayed from 'utils/functions/delayed.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import { isAnyError } from 'utils/predicates/errors.util';
import isFn from 'utils/predicates/function/isFn.util';
import isPositiveInteger from 'utils/predicates/number/isPositiveInteger.util';

const L = logger(['usePrepare', LT_FETCH]);

const USE_SPAM_LOG = false;

const configure = (options) => {
  const FTAG = 'configure()';

  const configurator = options?.configurator;

  const isValid = isFn(configurator);
  if (!isValid) {
    L.debug(FTAG, 'Invalid configurator:', configurator);
  }

  const configuratorOptions = {
    salonId: options?.salonId,
    ...(options?.init ?? {}),
  };

  const configuration = isValid ? configurator?.(configuratorOptions) : void 1;
  if (!configuration) {
    L.warn(FTAG, 'Invalid configuration:', configuration, 'for configuratorOptions:', configuratorOptions);
  }

  const api = configuration?.[AUTH_API];
  if (!api && configuration) {
    L.warn(FTAG, 'Invalid configuration[AUTH_API]:', api, 'for configuratorOptions:', configuratorOptions);
  }

  return configuration;
};

export const prepareReactQuery = ({ configurator, salonId, init: initFromOptions }) => {
  const FTAG = 'prepareReactQuery()';

  const cfg = configure({ configurator, salonId, init: initFromOptions });

  const refetchOnMount = initFromOptions?.refetchOnMount ?? false;
  const tag = fromAnyToTrimmed(cfg?.ATAG);
  const enabled = !cfg?.isMissingParams;

  const key = asArray(cfg?.queryKey).map((item) => (':salonId' === item ? salonId : item));

  const queryFn = async (ctx) => {
    const signal = FLAG.reactQueryAbortSignal ? ctx?.signal : void 1;
    const timeoutType = FLAG.reactQueryAbortPromise ? 'promise' : 'signal';

    const beOptions = { ...(cfg ?? {}), signal, timeoutType, store: STORE };
    const beResult = beVersion4(beOptions);
    const value = await beResult?.value;

    if (isAnyError(value)) {
      L.debug(FTAG, 'queryFn()', tag, beResult, '->', value);
      throw value;
    } else {
      return value ?? null; // queryFn must not return undefined
    }
  };

  /** @type function(options:any):Promise*/
  const mutationFn = async (variables) => {
    const combinedInit = { ...(initFromOptions ?? {}), ...(variables ?? {}) };
    const mutationConfiguration = configure({ configurator, salonId, init: combinedInit });
    const beOptions = { ...(mutationConfiguration ?? {}), store: STORE };

    const beResult = beVersion4(beOptions);
    const value = await beResult?.value;

    if (isAnyError(value)) {
      L.debug(FTAG, 'mutationFn()', tag, beResult, '->', value);
      throw value;
    } else {
      return value ?? null; // queryFn must not return undefined
    }
  };

  if (USE_SPAM_LOG) L.debug(FTAG, 'mutationFn()', tag, 'enabled:', enabled, 'cfg:', cfg);

  // When refetchOnMount is true, set staleTime to 0 so data is always considered stale
  const staleTime = refetchOnMount ? 0 : MS.minute;

  return {
    cfg,
    enabled,
    tag,
    queryKey: key,
    mutationKey: key,
    queryFn,
    mutationFn,
    staleTime,
    refetchOnMount,
  };
};

export const usePrepareFetcher = (configurator, options) => {
  const salonId = useCurrentSalonId();
  return useMemo(() => prepareReactQuery({ configurator, salonId, init: options }), [configurator, options, salonId]);
};

export const invalidateReactQuery = (options) => {
  const FTAG = 'invalidateReactQuery()';

  const tag = fromAnyToTrimmed(options?.tag);
  const remove = fromAnyToBool(options?.remove); // options?.remove !== false;
  const qr = options?.qr;
  const refetch = options?.refetch;

  if (!qr) L.warn(FTAG, tag, 'Invalid options.result:', qr);

  if (remove) {
    try {
      L.debug(FTAG, tag, 'removing', qr, '...');
      qr?.remove();
      L.debug(FTAG, tag, 'removed');
      if (!refetch) return true;
    } catch (e) {
      L.warn(FTAG, tag, e);
      if (!refetch) return false;
    }
  }

  if (!refetch) return void L.warn(FTAG, tag, 'Unhandled code path with refetch:', refetch);

  L.debug(FTAG, tag, 're-fetching...');

  try {
    const promise = qr?.refetch();

    promise
      .then((refetchResult) => L.debug(FTAG, tag, 'refetchResult:', refetchResult))
      .catch((e) => L.warn(FTAG, tag, e));

    return promise;
  } catch (e) {
    L.warn(FTAG, tag, e);
    return Promise.reject(e);
  }
};

export const usePrepareReadOnly = (configurator, options) => {
  const salonId = useCurrentSalonId();
  const prep = prepareReactQuery({ configurator, salonId, init: options });
  const qr = useQuery(prep);

  return useMemo(() => {
    const { isError, isLoading, data } = qr ?? {};
    const json = data?.json;
    return { data, json, prep, qr, qj: json, isLoading, isError };
  }, [prep, qr]);
};

export const usePrepareWriteOnly = (configurator, options) => {
  const prep = usePrepareFetcher(configurator, options);
  const mr = useMutation(prep);

  return useMemo(() => {
    const { isError, mutateAsync, isLoading, data } = mr ?? {};
    const json = data?.json;
    return { data, json, prep, mr, mj: json, isLoading, isError, up: mutateAsync, mutateAsync };
  }, [prep, mr]);
};

const usePrepareUpdater = (options) => {
  const delay = options?.delay;
  const mutateAsync = options?.mr?.mutateAsync;
  const qr = options?.qr;
  const refetch = options?.refetch ?? true;
  const remove = options?.remove ?? false;

  // noinspection UnnecessaryLocalVariableJS
  const up = useCallback(
    async (data) => {
      const result = await mutateAsync?.(data);

      if (isPositiveInteger(delay)) {
        delayed(delay, () => invalidateReactQuery({ qr, refetch, remove }));
      } else {
        invalidateReactQuery({ qr, refetch, remove });
      }

      return result;
    },
    [delay, mutateAsync, qr, refetch, remove],
  );

  return up;
};

export const usePrepareReadWrite = ({ reader, writer, rOptions, wOptions, rwOptions, uOptions }) => {
  const query = usePrepareReadOnly(reader, rOptions ?? rwOptions);
  const mutation = usePrepareWriteOnly(writer, wOptions ?? rwOptions);

  const up = usePrepareUpdater({ ...(uOptions ?? void 1), qr: query?.qr, mr: mutation?.mr });

  return useMemo(() => {
    const { qr, prep: qp, json: qj, data: qd, isLoading: qIsLoading, isError: qIsError } = query ?? {};
    const { mr, prep: mp, json: mj, data: md, isLoading: mIsLoading, isError: mIsError } = mutation ?? {};

    const isLoading = qIsLoading || mIsLoading;
    const isError = qIsError || mIsError;

    // short names for "query"/"mutation", "read", "write", "loading", "error" fields as prefixes
    // combined with "json", "data", "prep", "result" as suffixes
    const RF = { qj, qd, qp, rj: qj, rd: qd, rp: qp, qr, readResult: qr };
    const WF = { mj, md, mp, wj: mj, wd: md, wp: mp, mr, writeResult: mr };
    const LF = { isLoading, qIsLoading, mIsLoading, rIsLoading: qIsLoading, wIsLoading: mIsLoading };
    const EF = { isError, qIsError, mIsError, rIsError: qIsError, wIsError: mIsError };

    return { ...RF, ...WF, ...LF, ...EF, up, updater: up };
  }, [query, mutation, up]);
};

export const usePrepareCacheDataUpdate = (options) => {
  const FTAG_1 = 'usePrepareCacheDataUpdate()';

  const tag = fromAnyToTrimmed(options?.tag);

  const queryClient = useQueryClient();
  const setQueryData = queryClient?.setQueryData ?? alwaysNoop;

  return useCallback(
    (...outerArgs) => {
      try {
        const [queryKey, updater, ...rest] = outerArgs;

        const wrapper = function wrapper(...innerArgs) {
          const result = updater.apply(this, innerArgs);
          L.debug(FTAG_1, tag, 'wrapper()', 'queryKey:', queryKey, '|', result, '<-', ...innerArgs);
          return result;
        };

        return setQueryData.call(queryClient, queryKey, wrapper, ...rest);
      } catch (e) {
        L.warn(FTAG_1, tag, 'setQueryDataLogged()', 'outerArgs:', outerArgs, 'error:', e);
      }
    },
    [queryClient, setQueryData, tag],
  );
};
